/**
 * Odds - Consistent odds value display
 *
 * Displays odds in cents (46¢), American (+115), or dual (46¢ | +115) format.
 * Color coding is based on cents threshold (<=50 green, >=51 red),
 * regardless of output format.
 */

import { memo } from 'react';
import { cn } from '@/lib/infra/utils';
import {
  centsToAmericanOdds as canonicalCentsToAmerican,
  formatAmericanOdds,
} from '@/lib/pricing/american';

interface OddsProps {
  /** The price in cents (0-100) */
  cents: number | null | undefined;

  /** Display format */
  format: 'cents' | 'american' | 'dual';

  /**
   * Color code using cents threshold (<=50 green, >=51 red)
   * Default: true
   */
  colorize?: boolean;

  /** When true, renders with muted opacity to indicate stale/outdated data */
  stale?: boolean;

  /** Text to show when value is null/undefined (default: '—') */
  nullText?: string;

  /** Additional CSS classes */
  className?: string;
}

/**
 * Convert cents (probability) to American odds. Thin delegating wrapper
 * around {@link canonicalCentsToAmerican} / {@link formatAmericanOdds}
 * in lib/pricing/american — keeps the local {value, display} shape that
 * this component's render path expects, while routing the math through
 * the single canonical implementation (which uses the "+100" convention
 * at exactly 50¢, matching the dominant trading-surface convention).
 */
function centsToAmericanOdds(cents: number): { value: number; display: string } | null {
  const value = canonicalCentsToAmerican(cents);
  if (value == null) return null;
  return { value, display: formatAmericanOdds(value) };
}

function getCentsThresholdColorClass(cents: number): string {
  return cents <= 50 ? 'text-positive' : 'text-negative';
}

/**
 * Renders odds with proper formatting and styling
 */
export const Odds = memo(function Odds({
  cents,
  format,
  colorize,
  stale,
  nullText = '—',
  className,
}: OddsProps) {
  const staleClass = stale ? 'opacity-40' : '';

  // Handle null/undefined
  if (cents === null || cents === undefined || !Number.isFinite(cents)) {
    return <span className={cn('text-muted-foreground', staleClass, className)}>{nullText}</span>;
  }

  const shouldColorize = colorize ?? true;
  const thresholdColorClass = shouldColorize ? getCentsThresholdColorClass(cents) : '';

  // Cents format - threshold-colored display
  if (format === 'cents') {
    return (
      <span className={cn(thresholdColorClass, staleClass, className)}>{Math.round(cents)}¢</span>
    );
  }

  if (format === 'dual') {
    const american = centsToAmericanOdds(cents);
    if (!american) {
      return <span className={cn('text-muted-foreground', staleClass, className)}>{nullText}</span>;
    }
    return (
      <span className={cn(thresholdColorClass, staleClass, className)}>
        {Math.round(cents)}¢ | {american.display}
      </span>
    );
  }

  // American format
  const american = centsToAmericanOdds(cents);
  if (!american) {
    return <span className={cn('text-muted-foreground', staleClass, className)}>{nullText}</span>;
  }

  return <span className={cn(thresholdColorClass, staleClass, className)}>{american.display}</span>;
});

export default Odds;
